import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole, useRealmRoles } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import {
  ReportingGaugesDrawer,
  reportingEndpoints,
  useReportingGauges,
} from "@/features/reporting";
import { useCompanySubscriptions } from "@/features/fee-engine/api/use-company-subscriptions";
import type { CompanySubscriptionFilter } from "@/features/fee-engine/api/dto";
import {
  CompanySubscriptionFormDialog,
  companySubscriptionsColumns,
  type CompanySubscriptionRow,
} from "@/features/fee-engine/components";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";

const GAUGE_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

const FEE_PAST_DUE_GAUGE = ["flashpay_fee_subscriptions_past_due"] as const;

const toCompanySubscriptionFilter = (
  filters: ColumnFiltersState
): CompanySubscriptionFilter => {
  const filter: CompanySubscriptionFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "companyPartyId") filter.companyPartyId = value;
    if (id === "status")
      filter.status = value as CompanySubscriptionFilter["status"];
  }
  return filter;
};

/**
 * Liste paginée des abonnements entreprise
 * (`POST /CompanySubscriptions/all`, guide §6.5). Écriture réservée aux
 * rôles flashpay-superadmin / flashpay-admin.
 */
export function CompanySubscriptionsPage() {
  const { t } = useFeeEngineTranslation();
  const realmRoles = useRealmRoles();
  const canGauges = GAUGE_ROLES.some((role) => realmRoles.includes(role));
  const feeGauges = useReportingGauges("fee-engine", canGauges);
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toCompanySubscriptionFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useCompanySubscriptions(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("fees.subscriptions.title")}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <RequireRole
            roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
          >
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 w-4 h-4" />
              {t("fees.subscriptions.create")}
            </Button>
          </RequireRole>
          {canGauges ? (
            <ReportingGaugesDrawer
              gauges={feeGauges.data ?? []}
              names={FEE_PAST_DUE_GAUGE}
              provenanceEndpoints={[reportingEndpoints["fee-engine"].gauges]}
            />
          ) : null}
        </div>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter(
          (s): s is CompanySubscriptionRow => Boolean(s.id)
        )}
        columnsClar={companySubscriptionsColumns}
        title={t("fees.subscriptions.title")}
        exportFileName="company-subscriptions"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <CompanySubscriptionFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </section>
  );
}
